import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import api from '../services/api';

const ChangePasswordScreen = ({ navigation }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPasswordFocused, setCurrentPasswordFocused] = useState(false);
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [showPasswordHint, setShowPasswordHint] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (key, value) => {
    setFormData(prevData => ({ ...prevData, [key]: value }));
  };

  const showMessage = (title, message, onOk) => {
    if (Platform.OS === 'web') {
      alert(message);
      if (onOk) onOk();
    } else {
      Alert.alert(title, message, onOk ? [{ text: 'OK', onPress: onOk }] : undefined);
    }
  };

  const validateForm = () => {
    console.log('Validating form data:', formData);
    
    // Check required fields
    if (!formData.currentPassword || formData.currentPassword.trim() === '') {
      console.log('Validation failed: Current Password empty');
      showMessage('Validation Error', 'Current Password is required.');
      return false;
    }

    if (!formData.newPassword || formData.newPassword.trim() === '') {
      console.log('Validation failed: New Password empty');
      showMessage('Validation Error', 'New Password is required.');
      return false;
    }

    if (!formData.confirmPassword || formData.confirmPassword.trim() === '') {
      console.log('Validation failed: Confirm Password empty');
      showMessage('Validation Error', 'Confirm Password is required.');
      return false;
    }

    // Password validation (same as Mediator Registration)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
    if (!passwordRegex.test(formData.newPassword)) {
      showMessage(
        'Validation Error',
        'New Password must be at least 8 characters with uppercase, lowercase, number, and special character.'
      );
      return false;
    }

    // Check if new password matches confirm password
    if (formData.newPassword !== formData.confirmPassword) {
      showMessage('Validation Error', 'New Password and Confirm Password do not match.');
      return false;
    }

    // Check if new password is different from current password
    if (formData.currentPassword === formData.newPassword) {
      showMessage('Validation Error', 'New Password must be different from Current Password.');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const payload = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      };

      const response = await api.post('/api/Auth/change-password', payload);
      
      showMessage(
        'Success',
        'Password changed successfully!',
        () => {
          // Clear form and navigate back
          setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
          navigation.goBack();
        }
      );
    } catch (error) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to change password';
      showMessage('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleCancel} style={styles.backButton}>
          <SvgIcon name="arrow-back" size={24} color={colors.white} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Change Password</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.formContainer}>
          <Text style={styles.infoText}>
            Please enter your current password and choose a new password.
          </Text>

          {/* Current Password */}
          <View style={styles.inputWrapper}>
            <Text style={styles.floatingLabel}>Current Password *</Text>
            <View style={[
              styles.passwordContainer, 
              currentPasswordFocused && styles.passwordContainerFocused
            ]}>
              <TextInput
                style={styles.passwordInput}
                placeholder="Enter current password"
                placeholderTextColor="#9CA3AF"
                value={formData.currentPassword}
                onChangeText={(v) => handleChange('currentPassword', v)}
                secureTextEntry={!showCurrentPassword}
                onFocus={() => setCurrentPasswordFocused(true)}
                onBlur={() => setCurrentPasswordFocused(false)}
              />
              <TouchableOpacity 
                onPress={() => setShowCurrentPassword(!showCurrentPassword)}
                style={styles.eyeIcon}
              >
                <SvgIcon 
                  name={showCurrentPassword ? 'eye' : 'eye-off'} 
                  size={20} 
                  color="#9CA3AF" 
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* New Password */}
          <View style={styles.inputWrapper}>
            <Text style={styles.floatingLabel}>New Password *</Text>
            <View style={[
              styles.passwordContainer, 
              newPasswordFocused && styles.passwordContainerFocused
            ]}>
              <TextInput
                style={styles.passwordInput}
                placeholder="Enter new password"
                placeholderTextColor="#9CA3AF"
                value={formData.newPassword}
                onChangeText={(v) => handleChange('newPassword', v)}
                secureTextEntry={!showNewPassword}
                onFocus={() => {
                  setNewPasswordFocused(true);
                  setShowPasswordHint(true);
                }}
                onBlur={() => {
                  setNewPasswordFocused(false);
                  setShowPasswordHint(false);
                }}
              />
              <TouchableOpacity 
                onPress={() => setShowNewPassword(!showNewPassword)}
                style={styles.eyeIcon}
              >
                <SvgIcon 
                  name={showNewPassword ? 'eye' : 'eye-off'} 
                  size={20} 
                  color="#9CA3AF" 
                />
              </TouchableOpacity>
            </View>
          </View>

          {showPasswordHint && (
            <Text style={styles.passwordHint}>
              Password must be at least 8 characters with uppercase, lowercase, number, and special character.
            </Text>
          )}

          {/* Confirm Password */}
          <View style={styles.inputWrapper}>
            <Text style={styles.floatingLabel}>Confirm New Password *</Text>
            <View style={[
              styles.passwordContainer, 
              confirmPasswordFocused && styles.passwordContainerFocused
            ]}>
              <TextInput
                style={styles.passwordInput}
                placeholder="Confirm new password"
                placeholderTextColor="#9CA3AF"
                value={formData.confirmPassword}
                onChangeText={(v) => handleChange('confirmPassword', v)}
                secureTextEntry={!showConfirmPassword}
                onFocus={() => setConfirmPasswordFocused(true)}
                onBlur={() => setConfirmPasswordFocused(false)}
              />
              <TouchableOpacity 
                onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.eyeIcon}
              >
                <SvgIcon 
                  name={showConfirmPassword ? 'eye' : 'eye-off'} 
                  size={20} 
                  color="#9CA3AF" 
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Buttons */}
          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={handleCancel}
              disabled={isLoading}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.button, styles.submitButton]}
              onPress={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color={colors.white} />
              ) : (
                <Text style={styles.buttonText}>Update Password</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingTop: 50,
    backgroundColor: colors.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.accent,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.white,
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  formContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  infoText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 24,
    lineHeight: 20,
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  floatingLabel: {
    position: 'absolute',
    top: -10,
    left: 12,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 4,
    fontSize: 12,
    fontWeight: '500',
    color: colors.secondary,
    zIndex: 1,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    paddingRight: 12,
  },
  passwordContainerFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  passwordInput: {
    flex: 1,
    padding: 12,
    paddingTop: 14,
    fontSize: 14,
    color: colors.primary,
  },
  eyeIcon: {
    padding: 4,
  },
  passwordHint: {
    fontSize: 12,
    color: colors.secondary,
    marginBottom: 12,
    marginTop: -8,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  button: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
  },
  submitButton: {
    backgroundColor: colors.secondary,
  },
  cancelButton: {
    backgroundColor: '#6c757d',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ChangePasswordScreen;
